import { Building2, House, Mail, Menu, MessageCircle, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { RoadmapFeatureCard } from "@/components/RoadmapFeatureCard";
import { MobileInstallBanner } from "@/components/MobileInstallBanner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AGENT_PORTAL_WORDMARK_URL, BRAND_TAGLINE, HEADER_WORDMARK_URL, NAV_LINKS, SITE_LOGO_URL } from "@/data/portalConfig";
import { useSiteBranding } from "@/context/SiteContext";

export const PublicLayout = ({ onLogout, user }) => {
  const [showPersonaLabels, setShowPersonaLabels] = useState(false);
  const [isMobileHeaderCompressed, setIsMobileHeaderCompressed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { branding, buildBrandedPath, getWhatsAppHref, isAgentPath, isSiteContextLoading, leadFormPath, siteContext } = useSiteBranding();
  const routeIsAgentPath = location.pathname.startsWith("/agent/");
  const brandLogo = branding.team_logo_url || SITE_LOGO_URL;
  const agentTheme = branding.agent_theme || "classic";
  const isSignatureBrand = isAgentPath && agentTheme === "signature";
  const isSignatureInviteOnly = isSignatureBrand && siteContext.invite_only;
  const isRootSalesPage = location.pathname === "/";
  const isForAgentsOnboardingPage = location.pathname.startsWith("/for-agents/onboarding");
  const isForAgentsPage = location.pathname.startsWith("/for-agents");
  const isAdvisorRoute = location.pathname === "/advisor" || location.pathname.endsWith("/advisor");
  const isListingDetailRoute = location.pathname.includes("/listings/");
  const isListingsHubRoute = location.pathname.includes("/listings") || location.pathname.includes("/buy") || location.pathname.includes("/rent") || location.pathname.includes("/new-launches") || location.pathname.includes("/district/");
  const isFinancialsRoute = location.pathname.includes("/financials") || location.pathname.includes("/calculators");
  const isInsightsRoute = location.pathname.includes("/news") || location.pathname.includes("/insights");
  const isInstitutionalTierRoute = location.pathname === "/" || location.pathname.startsWith("/for-agents");
  const showB2bFooterLinks = location.pathname === "/" || location.pathname.startsWith("/for-agents");
  const useMainSitePalette = isRootSalesPage || isForAgentsOnboardingPage;
  const dashboardHref = ["agent", "pending_agent"].includes(user?.role) && user?.subdomain_slug ? `/agent/${user.subdomain_slug}/dashboard` : "/admin";
  const advisorFirstName = String((branding.name || "Advisor").split(" ")[0] || "Advisor");
  const agentPortalNavigationLinks = useMemo(
    () => ([
      { label: "Home", href: buildBrandedPath("/"), global: true },
      { label: "Listings", href: buildBrandedPath("/listings"), global: true },
      { label: "Buy", href: buildBrandedPath("/buy"), global: true },
      { label: "Rent", href: buildBrandedPath("/rent"), global: true },
      { label: "New Launches", href: buildBrandedPath("/new-launches"), global: true },
      { label: "Financials", href: buildBrandedPath("/financials"), global: true },
      { label: "Market Insights", href: buildBrandedPath("/insights"), global: true },
      { label: "About", href: buildBrandedPath("/about"), global: true },
    ]),
    [buildBrandedPath],
  );
  const navigationLinks = routeIsAgentPath ? agentPortalNavigationLinks : NAV_LINKS;
  const navClass = (item) => ({ isActive }) => {
    if (item.emphasis) {
      return `public-nav-link inline-flex min-h-[44px] items-center justify-center whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-bold leading-none transition duration-300 shadow-[0_12px_28px_rgba(179,147,93,0.18)] ${
        isActive
          ? "bg-[#c2a36b] text-[#001935]"
          : "bg-[#b3935d] text-[#001935] hover:-translate-y-0.5 hover:bg-[#c2a36b]"
      }`;
    }

    return `public-nav-link inline-flex min-h-[44px] items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold leading-none transition duration-300 ${
      isActive
        ? "bg-[#D4AF37] text-[#2C2C2C]"
        : isSignatureInviteOnly
          ? "text-[#1A1A1A] hover:bg-[#1A1A1A]/6 hover:text-[#1A1A1A]"
          : !useMainSitePalette && isForAgentsPage
            ? "text-slate-100 hover:bg-white/10 hover:text-[#F5D98C]"
            : "text-slate-100 hover:bg-white/10 hover:text-[#F5D98C]"
    }`;
  };
  const headerClassName = isSignatureInviteOnly
    ? "fixed inset-x-0 top-0 z-50 border-b border-[#D4AF37]/18 bg-[rgba(242,237,228,0.82)] backdrop-blur-md shadow-[0_12px_30px_rgba(90,75,53,0.08)]"
    : !useMainSitePalette && isForAgentsPage
    ? "fixed inset-x-0 top-0 z-50 border-b border-[#b3935d]/18 bg-[linear-gradient(180deg,rgba(0,25,53,0.98),rgba(4,33,67,0.94))] shadow-[0_18px_40px_rgba(0,25,53,0.24)]"
    : "fixed inset-x-0 top-0 z-50 border-b border-[#b3935d]/18 bg-[linear-gradient(180deg,rgba(0,25,53,0.98),rgba(4,33,67,0.94))] shadow-[0_18px_40px_rgba(0,25,53,0.24)]";
  const pageClassName = isSignatureInviteOnly
    ? "min-h-screen bg-[radial-gradient(circle_at_top,#f7f1e8_0%,#f2ede4_42%,#e9e1d5_100%)] text-[#1A1A1A]"
    : isSignatureBrand
      ? "min-h-screen bg-[#f4efe7] text-[#001935]"
      : "min-h-screen bg-[radial-gradient(circle_at_top,#fbf7ee_0%,#f8f3e7_44%,#eef2f6_100%)] text-[#001935]";
  const footerClassName = !useMainSitePalette && isForAgentsPage
    ? "border-t border-[#D7C39A]/50 bg-[#F5F2EA] pb-28 pt-16 text-[#333333] md:pb-16"
    : "border-t border-[#D4AF37]/70 bg-[#F5F2EA] pb-28 pt-16 text-[#333333] md:pb-16";
  const footerAccentClassName = "text-[#D4AF37]";
  const footerBodyClassName = !useMainSitePalette && isForAgentsPage ? "text-[#333333]" : "text-[#333333]";
  const mobileHeaderDensity = useMemo(() => {
    if (isRootSalesPage || isForAgentsPage) return "light";
    if (isAdvisorRoute || isListingsHubRoute || isListingDetailRoute || isFinancialsRoute) return "heavy";
    if (isInsightsRoute || routeIsAgentPath) return "balanced";
    return "balanced";
  }, [isAdvisorRoute, isFinancialsRoute, isForAgentsPage, isInsightsRoute, isListingDetailRoute, isListingsHubRoute, isRootSalesPage, routeIsAgentPath]);
  const mobileHeaderCompactThreshold = useMemo(() => {
    if (mobileHeaderDensity === "heavy") return 18;
    if (mobileHeaderDensity === "light") return 44;
    return 28;
  }, [mobileHeaderDensity]);
  const forAgentsPrimaryHref = "/signup?plan=pro";
  const forAgentsDemoHref = "/agent/clive";
  const buyerJourneyHref = isForAgentsPage ? buildBrandedPath("/") : `${buildBrandedPath("/")}#consumer-lifecycle-section`;
  const agentJourneyHref = isForAgentsPage ? "#for-agents-feature-section" : "/for-agents";
  const headerBrandSurfaceClassName = isSignatureInviteOnly
    ? "bg-transparent"
    : routeIsAgentPath
      ? "bg-transparent"
      : "border border-[#D4AF37]/18 bg-white/8";
  const headerBrandTextClassName = isSignatureInviteOnly ? "text-[#1A1A1A]" : "text-[#F5D98C]";
  const mobileNavItems = useMemo(() => {
    if (routeIsAgentPath) {
      return [
        { key: "home", label: "Home", icon: House, href: buildBrandedPath("/"), isActive: location.pathname === buildBrandedPath("/") },
        {
          key: "listings",
          label: "Listings",
          icon: Building2,
          href: buildBrandedPath("/listings"),
          isActive: location.pathname.includes("/listings") || location.pathname.includes("/district/"),
        },
        {
          key: "buy",
          label: "Buy",
          icon: Search,
          href: buildBrandedPath("/buy"),
          isActive: location.pathname.includes("/buy") || location.pathname.includes("/rent"),
        },
        {
          key: "new-launches",
          label: "New Launches",
          icon: Search,
          href: buildBrandedPath("/new-launches"),
          isActive: location.pathname.includes("/new-launches"),
        },
      ];
    }

    return [
      { key: "home", label: "Home", icon: House, href: buildBrandedPath("/"), isActive: location.pathname === buildBrandedPath("/") },
      {
        key: "listings",
        label: "Listings",
        icon: Building2,
        href: buildBrandedPath("/listings"),
        isActive: location.pathname.includes("/listings") || location.pathname.includes("/buy") || location.pathname.includes("/rent") || location.pathname.includes("/new-launches") || location.pathname.includes("/district/"),
      },
      { key: "contact", label: "Contact", icon: Mail, href: leadFormPath, isActive: location.pathname.includes("/contact") },
      { key: "for-agents", label: "Agents", icon: Search, href: "/for-agents", isActive: location.pathname.startsWith("/for-agents") },
    ];
  }, [buildBrandedPath, leadFormPath, location.pathname, routeIsAgentPath]);

  useEffect(() => {
    if (!(isRootSalesPage || isForAgentsPage)) {
      setShowPersonaLabels(false);
      return undefined;
    }

    const handleScroll = () => {
      setShowPersonaLabels(window.scrollY > 180);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isForAgentsPage, isRootSalesPage]);

  useEffect(() => {
    const handleMobileHeaderCompression = () => {
      if (window.innerWidth >= 768) {
        setIsMobileHeaderCompressed(false);
        return;
      }

      setIsMobileHeaderCompressed(window.scrollY > mobileHeaderCompactThreshold);
    };

    handleMobileHeaderCompression();
    window.addEventListener("scroll", handleMobileHeaderCompression, { passive: true });
    window.addEventListener("resize", handleMobileHeaderCompression);

    return () => {
      window.removeEventListener("scroll", handleMobileHeaderCompression);
      window.removeEventListener("resize", handleMobileHeaderCompression);
    };
  }, [mobileHeaderCompactThreshold]);

  if (routeIsAgentPath && isSiteContextLoading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,#f7f1e8_0%,#f2ede4_42%,#e9e1d5_100%)] text-[#1A1A1A]" data-testid="agent-route-loading-shell">
        <div className={`global-mobile-header mobile-header-density-${mobileHeaderDensity} fixed inset-x-0 top-0 z-50 border-b border-[#D4AF37]/18 bg-[rgba(242,237,228,0.82)] backdrop-blur-md shadow-[0_12px_30px_rgba(90,75,53,0.08)]`}>
          <div className="public-header-shell mx-auto grid max-w-7xl grid-cols-[auto,1fr,auto] items-center gap-5 px-6 py-3 md:px-10 lg:px-12">
            <div className="public-header-brand flex items-center gap-3" data-testid="agent-route-loading-brand">
              <div className={`inline-flex items-center gap-3 rounded-full px-3 py-2 ${headerBrandSurfaceClassName}`} data-testid="agent-route-loading-brand-lockup">
                <img alt="SGPropPortal.com logo" className="h-9 object-contain object-left md:h-10" data-testid="agent-route-loading-brand-image" decoding="async" loading="lazy" src={AGENT_PORTAL_WORDMARK_URL} style={{ width: "170px", minWidth: "170px", maxWidth: "170px" }} />
              </div>
            </div>
          </div>
        </div>
        <main className="public-main-shell pb-28 pt-20 md:pb-0 md:pt-24">
          <section className="section-shell py-10 md:py-14" data-testid="agent-route-loading-main">
            <div className="relative overflow-hidden rounded-[40px] border border-[#D4AF37]/20 bg-[linear-gradient(180deg,rgba(212,175,55,0.06)_0%,rgba(255,255,255,0)_48%)] p-[1px] shadow-[0_34px_90px_rgba(90,75,53,0.16)]">
              <div className="relative overflow-hidden rounded-[39px] bg-[linear-gradient(145deg,#171717_0%,#1A1A1A_52%,#222222_100%)] px-6 py-12 md:px-10 md:py-16">
                <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
                  <div className="space-y-5 animate-pulse">
                    <div className="h-4 w-56 rounded-full bg-[#D4AF37]/25" />
                    <div className="h-16 w-full max-w-2xl rounded-[20px] bg-white/8" />
                    <div className="h-16 w-[80%] rounded-[20px] bg-white/8" />
                    <div className="h-5 w-[72%] rounded-full bg-white/10" />
                    <div className="h-12 w-64 rounded-full bg-[#D4AF37]/18" />
                  </div>
                  <div className="rounded-[32px] border border-[#D4AF37]/16 bg-[#1f1f1f]/92 p-6 backdrop-blur animate-pulse">
                    <div className="rounded-[24px] border border-[#D4AF37]/18 bg-[#2A2724] p-5">
                      <div className="h-4 w-28 rounded-full bg-[#D4AF37]/25" />
                      <div className="mt-4 h-10 w-[85%] rounded-[14px] bg-white/8" />
                    </div>
                    <div className="mt-4 rounded-[24px] border border-white/10 bg-[#151515] p-5">
                      <div className="h-4 w-24 rounded-full bg-[#D4AF37]/25" />
                      <div className="mt-4 h-6 w-44 rounded-[10px] bg-white/8" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className={`${pageClassName} ${isAgentPath ? `agent-theme-${agentTheme}` : ""}`} data-agent-theme={isAgentPath ? agentTheme : "classic"} data-portal-surface={isAgentPath ? "agent" : "public"} data-signature-invite-only={isSignatureInviteOnly ? "true" : "false"}>
      <header className={`${headerClassName} global-mobile-header mobile-header-density-${mobileHeaderDensity} ${isMobileHeaderCompressed ? "is-mobile-header-compact" : ""}`} data-glass-card="true" data-signature-invite-only={isSignatureInviteOnly ? "true" : "false"}>
        <div className="public-header-shell mx-auto grid max-w-7xl grid-cols-[auto,1fr,auto] items-center gap-5 px-6 py-3 md:px-10 lg:px-12">
          <NavLink className="inline-flex items-center" data-testid="site-header-wordmark-link" to={buildBrandedPath("/")}>
            {routeIsAgentPath ? (
              <div className={`inline-flex items-center gap-3 rounded-full px-3 py-2 ${headerBrandSurfaceClassName}`} data-testid="site-header-brand-lockup">
                <img alt="SGPropPortal.com logo" className="h-9 object-contain object-left md:h-10" data-testid="site-header-brand-image" decoding="async" loading="lazy" src={AGENT_PORTAL_WORDMARK_URL} style={{ width: "170px", minWidth: "170px", maxWidth: "170px" }} />
              </div>
            ) : (
              <img alt="SG Prop Portal" className="h-11 w-auto object-contain md:h-12" data-testid="site-header-wordmark-image" decoding="async" loading="lazy" src={HEADER_WORDMARK_URL} />
            )}
          </NavLink>

          <nav className="hidden items-center justify-center gap-1.5 lg:flex xl:gap-2" data-testid="desktop-navigation">
            {navigationLinks.map((item) => (
              <NavLink className={navClass(item)} data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`} key={item.href} to={item.global ? item.href : buildBrandedPath(item.href)}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="public-header-actions hidden items-center justify-end gap-4 lg:flex">
            {user ? (
              <>
                <NavLink className={`outline-button ${isSignatureInviteOnly ? "!border-[#1A1A1A]/16 !text-[#1A1A1A] hover:!bg-[#1A1A1A]/6" : ""}`} data-testid="header-admin-link" to={dashboardHref}>
                  Dashboard
                </NavLink>
                <button className="gold-button" data-testid="header-logout-button" onClick={onLogout} type="button">
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <NavLink className="outline-button whitespace-nowrap" data-testid="header-contact-form-link" to={leadFormPath}>
                  <Mail className="mr-2 h-4 w-4" /> {routeIsAgentPath ? `Contact ${advisorFirstName}` : "Contact Us"}
                </NavLink>
                {routeIsAgentPath ? (
                  <a className="gold-button whitespace-nowrap" data-testid="header-whatsapp-direct-link" href={getWhatsAppHref(`Hello ${advisorFirstName}, I would like help with my property search.`)} rel="noreferrer" target="_blank">
                    <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp {advisorFirstName}
                  </a>
                ) : null}
              </>
            )}
          </div>

          <div className="flex items-center justify-end lg:hidden">
            <button className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/8 text-white transition hover:bg-white/12" data-testid="mobile-menu-button" onClick={() => setIsMobileMenuOpen(true)} type="button">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Open navigation menu</span>
            </button>
          </div>
        </div>

        {showPersonaLabels ? (
          <div className={`public-header-persona-bar border-t px-6 py-2 md:px-10 lg:px-12 ${isSignatureInviteOnly ? "border-[#1A1A1A]/10 bg-[rgba(242,237,228,0.92)]" : !useMainSitePalette && isForAgentsPage ? "border-[#b3935d]/14 bg-[linear-gradient(180deg,rgba(0,25,53,0.98),rgba(4,33,67,0.94))]" : "border-white/10 bg-[linear-gradient(180deg,rgba(0,25,53,0.98),rgba(4,33,67,0.94))]"}`} data-testid="sticky-header-persona-labels">
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-2">
              <a
                className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] transition ${isSignatureInviteOnly ? "border-[#1A1A1A]/12 text-[#1A1A1A] hover:bg-[#1A1A1A]/6" : !useMainSitePalette && isForAgentsPage ? "border-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/10" : "border-white/10 text-white hover:bg-white/10"}`}
                data-testid="sticky-header-for-buyers-label"
                href={buyerJourneyHref}
              >
                For Buyers
              </a>
              <a
                className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] transition ${isSignatureInviteOnly ? "border-[#1A1A1A]/12 text-[#1A1A1A] hover:bg-[#1A1A1A]/6" : !useMainSitePalette && isForAgentsPage ? "border-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/10" : "border-white/10 text-white hover:bg-white/10"}`}
                data-testid="sticky-header-for-agents-label"
                href={agentJourneyHref}
              >
                For Agents
              </a>
            </div>
          </div>
        ) : null}
      </header>

      <main className="public-main-shell pb-36 pt-20 md:pb-0 md:pt-24" data-signature-invite-only={isSignatureInviteOnly ? "true" : "false"}>
        <MobileInstallBanner />
        <Outlet />
      </main>

      <a
        className="fixed bottom-24 right-4 z-[72] inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#f2dfb5] bg-[#b3935d] text-[#001935] shadow-[0_18px_34px_rgba(0,25,53,0.28)] transition duration-300 hover:-translate-y-0.5 hover:bg-[#c4a36b] md:bottom-28 md:right-5"
        data-testid="floating-whatsapp-button"
        href={getWhatsAppHref()}
        rel="noreferrer"
        target="_blank"
        aria-label={routeIsAgentPath ? `WhatsApp ${advisorFirstName}` : "WhatsApp Us"}
      >
        <MessageCircle className="h-6 w-6" />
      </a>

      <RoadmapFeatureCard />

      <footer className={footerClassName}>
        <div className="section-shell py-0">
          <div className="grid gap-10 lg:grid-cols-3">
            <div className="space-y-4" data-testid="footer-brand-column">
              <img alt="SGPropPortal.com logo" className="signature-brand-logo h-14 w-auto object-contain" data-testid="footer-logo-image" decoding="async" loading="lazy" src={isAgentPath ? AGENT_PORTAL_WORDMARK_URL : HEADER_WORDMARK_URL} />
              {isAgentPath ? (
                <div className="inline-flex max-w-full items-center gap-3 rounded-full border border-[#D4AF37]/18 bg-white/70 px-3 py-2" data-testid="footer-agent-brand-chip">
                  {brandLogo ? <img alt={`${branding.name} brand logo`} className="h-9 w-9 rounded-full object-contain" data-testid="footer-agent-brand-chip-logo" decoding="async" loading="lazy" src={brandLogo} /> : <div className="signature-house-mark flex h-9 w-9 items-center justify-center rounded-full" data-testid="footer-agent-brand-chip-fallback"><House className="h-5 w-5 text-[#D4AF37]" strokeWidth={2.4} /></div>}
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid="footer-agent-brand-chip-label">Portal presented by</p>
                    <p className="truncate text-sm font-semibold text-[#333333]" data-testid="footer-agent-brand-chip-name">{branding.name}</p>
                  </div>
                </div>
              ) : null}
              <p className={`signature-brand-name font-serif text-3xl font-semibold ${!useMainSitePalette && isInstitutionalTierRoute ? "text-[#D4AF37]" : "text-[#333333]"}`}>SGPropPortal.com</p>
              <p className={`max-w-md text-sm leading-7 ${footerBodyClassName}`}>{BRAND_TAGLINE}</p>
            </div>

            <div className={`space-y-3 text-[11px] leading-6 ${footerBodyClassName}`} data-testid="footer-compliance-column">
              <p className={`text-sm font-semibold uppercase tracking-[0.18em] ${footerAccentClassName}`}>Team</p>
              <div data-testid="footer-compliance-block">
                <p>{branding.name}</p>
                <p className={footerAccentClassName}>{branding.cea_registration_number ? `CEA ${branding.cea_registration_number}` : "Property Advisory Team"}</p>
                <p>Buyer, seller, and new-launch guidance across Singapore.</p>
                <p className={footerAccentClassName}>{isAgentPath ? "Private agent portfolio" : "WhatsApp-first response desk"}</p>
              </div>
            </div>

            <div className="space-y-4" data-testid="footer-contact-column">
              <p className={`text-sm font-semibold uppercase tracking-[0.18em] ${footerAccentClassName}`}>Contact</p>
              <div className={`space-y-3 text-sm ${footerBodyClassName}`}>
                <a className="inline-flex items-center gap-3 transition hover:text-[#8b6d16]" data-testid="footer-owner-mobile" href={getWhatsAppHref()} rel="noreferrer" target="_blank">
                  <MessageCircle className="h-4 w-4 text-[#D4AF37]" /> WhatsApp: {branding.whatsapp_display}
                </a>
                <a className="inline-flex items-center gap-3 transition hover:text-[#8b6d16]" data-testid="footer-owner-email" href={`mailto:${branding.email}`}>
                  <Mail className="h-4 w-4 text-[#D4AF37]" /> Email: {branding.email}
                </a>
                {showB2bFooterLinks ? (
                  <>
                    <NavLink className="inline-flex items-center gap-3 transition hover:text-[#8b6d16]" data-testid="footer-for-agents-link" to="/for-agents">
                      <House className="h-4 w-4 text-[#D4AF37]" /> For Agents
                    </NavLink>
                    <NavLink className="inline-flex items-center gap-3 transition hover:text-[#8b6d16]" data-testid="footer-launch-your-portal-link" to="/signup?plan=pro">
                      <House className="h-4 w-4 text-[#D4AF37]" /> Launch Your Portal
                    </NavLink>
                    <NavLink className="inline-flex items-center gap-3 transition hover:text-[#8b6d16]" data-testid="footer-pricing-link" to="/pricing">
                      <House className="h-4 w-4 text-[#D4AF37]" /> Pricing
                    </NavLink>
                    <NavLink className="inline-flex items-center gap-3 transition hover:text-[#8b6d16]" data-testid="footer-referral-program-link" to="/for-agents#referral-program">
                      <House className="h-4 w-4 text-[#D4AF37]" /> Referral Program
                    </NavLink>
                    <p className={`text-xs leading-6 ${footerBodyClassName}`} data-testid="footer-referral-program-copy">
                      Alpha Investor recurring commission with first-click 60-day attribution.
                    </p>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          <div className={`mt-12 border-t ${!useMainSitePalette && isForAgentsPage ? "border-[#D4AF37]/20" : "border-[#D4AF37]/70"} pt-5`}>
            <p className={`text-[11px] leading-6 ${footerBodyClassName}`} data-testid="footer-disclaimer-text">
              Disclaimer: The information provided on SGPropPortal.com is for general informational purposes only and does not constitute a legal offer, contract, or investment advice. While we strive for accuracy, all property listings, prices, floor areas, and availability are subject to change without notice and should be independently verified. Visual representations and artist impressions are for illustrative purposes only. SGPropPortal.com and its associates disclaim all liability for any loss or damage arising from the use of this information. Artist impressions are for illustrative purposes only.
            </p>
            {siteContext.source_disclaimer ? <p className={`mt-3 text-[11px] ${!useMainSitePalette && isForAgentsPage ? "text-[#D4AF37]/60" : "text-[#6b6252]"}`} data-testid="footer-agent-source-disclaimer">{siteContext.source_disclaimer}</p> : null}
            <p className={`mt-4 text-[11px] ${footerBodyClassName}`} data-testid="footer-copyright-text">© 2026 SGPropPortal.com. All Rights Reserved.</p>
            <div className="mt-6 border-t border-white/5 pt-4 text-center font-sans text-[12px] text-[#001935]/60" data-testid="footer-mvicto-attribution">
              <p>
                Designed &amp; Developed by <a className="footer-mvicto-link transition" data-testid="footer-mvicto-link" href="https://mvicto.com" onMouseEnter={(event) => { event.currentTarget.style.color = "#D4AF37"; }} onMouseLeave={(event) => { event.currentTarget.style.color = "#001935"; }} rel="noreferrer" target="_blank">Mvicto Technologies</a>
              </p>
              <p className="mt-1" data-testid="footer-mvicto-rights">© All rights reserved. 2026</p>
            </div>
          </div>
        </div>
      </footer>

      <Sheet onOpenChange={setIsMobileMenuOpen} open={isMobileMenuOpen}>
        <SheetContent className="border-l border-[#D4AF37]/25 bg-[linear-gradient(180deg,rgba(0,25,53,0.98),rgba(4,33,67,0.96))] px-5 text-white sm:max-w-sm" data-testid="mobile-menu-sheet" side="right">
          <SheetHeader className="border-b border-white/10 pb-5">
            <SheetTitle className="font-[Cormorant_Garamond,serif] text-3xl font-bold text-white" data-testid="mobile-menu-title">
              {routeIsAgentPath ? `${advisorFirstName}'s portal` : "SG Prop Portal"}
            </SheetTitle>
            <SheetDescription className="text-sm leading-7 text-white/72" data-testid="mobile-menu-description">
              {routeIsAgentPath ? "Browse listings, move between routes quickly, and contact the advisor when you are ready." : "Explore the site and jump to the right next step quickly."}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 grid gap-3" data-testid="mobile-menu-links">
            {navigationLinks.map((item) => (
              <NavLink
                className={({ isActive }) => `rounded-[20px] border px-4 py-3 text-sm font-semibold transition ${isActive ? "border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#F5D98C]" : "border-white/10 bg-white/5 text-white hover:bg-white/10"}`}
                data-testid={`mobile-menu-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                key={`mobile-${item.href}`}
                onClick={() => setIsMobileMenuOpen(false)}
                to={item.global ? item.href : buildBrandedPath(item.href)}
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          <div className="mt-6 grid gap-3 border-t border-white/10 pt-6" data-testid="mobile-menu-actions">
            <NavLink className="outline-button !w-full !justify-center !border-white/20 !text-white hover:!bg-white/10" data-testid="mobile-menu-contact-link" onClick={() => setIsMobileMenuOpen(false)} to={leadFormPath}>
              <Mail className="mr-2 h-4 w-4" /> {routeIsAgentPath ? `Contact ${advisorFirstName}` : "Contact Us"}
            </NavLink>
            <a className="gold-button !w-full !justify-center" data-testid="mobile-menu-whatsapp-link" href={getWhatsAppHref(routeIsAgentPath ? `Hello ${advisorFirstName}, I would like help with my property search.` : undefined)} onClick={() => setIsMobileMenuOpen(false)} rel="noreferrer" target="_blank">
              <MessageCircle className="mr-2 h-4 w-4" /> {routeIsAgentPath ? `WhatsApp ${advisorFirstName}` : "WhatsApp Us"}
            </a>
          </div>
        </SheetContent>
      </Sheet>

      <nav className="public-mobile-bottom-nav fixed inset-x-3 bottom-3 z-50 md:hidden" data-glass-card="true" data-testid="mobile-bottom-navigation">
        <div className="public-mobile-bottom-nav-shell grid grid-cols-4 items-center gap-2 rounded-[26px] border border-[#D4AF37]/18 bg-[rgba(0,0,0,0.82)] px-3 py-3 shadow-[0_18px_50px_rgba(0,0,0,0.28)] backdrop-blur-xl">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink className={`public-mobile-bottom-nav-link flex flex-col items-center justify-center gap-1 rounded-[18px] px-2 py-2 text-[11px] font-semibold transition ${item.isActive ? "bg-[#D4AF37]/14 text-[#D4AF37]" : "text-white/72 hover:bg-white/6 hover:text-white"}`} data-testid={`mobile-bottom-nav-${item.key}`} key={item.key} to={item.href}>
                <Icon className={`h-5 w-5 ${item.isActive ? "text-[#D4AF37]" : "text-white/72"}`} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};